import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Send } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const OCCASION_OPTIONS = [
    "Proposal",
    "Bridal Shower",
    "Birthday Celebration",
    "Anniversary",
    "Date Night",
    "Other Celebration",
];

const PACKAGE_OPTIONS = [
    "The Intimate Date Night",
    "The Celebration Soirée",
    "The Grand Proposal",
    "Not sure yet",
];

const inputCls =
    "w-full bg-white border border-[#E8C4C1] rounded-xl px-4 py-3 text-[#2D230A] placeholder:text-[#6E5B3D]/50 focus:outline-none focus:ring-2 focus:ring-[#C18E8B]/50 focus:border-[#C18E8B] transition";

export const InquiryForm = ({ defaultPackage }) => {
    const [form, setForm] = useState({
        name: "",
        email: "",
        phone: "",
        occasion: "",
        date: "",
        city: "",
        guests: "",
        packageInterest: defaultPackage || "",
        message: "",
    });
    const [submitting, setSubmitting] = useState(false);
    const [sent, setSent] = useState(false);

    const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

    const submit = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        try {
            await axios.post(`${API}/inquiries`, {
                ...form,
                guests: form.guests ? Number(form.guests) : null,
            });
            setSent(true);
            toast.success("Inquiry sent! We'll be in touch within 24 hours.");
        } catch {
            toast.error("Something went wrong — please try again.");
        } finally {
            setSubmitting(false);
        }
    };

    if (sent) {
        return (
            <div
                data-testid="inquiry-success-message"
                className="bg-white border border-[#E8C4C1] rounded-3xl p-10 text-center shadow-sm"
            >
                <p className="font-accent text-xs uppercase tracking-[0.2em] text-[#C18E8B] mb-3">
                    Inquiry Received
                </p>
                <h3 className="font-display text-3xl text-[#2D230A]">Thank you, {form.name.split(" ")[0]}.</h3>
                <p className="mt-4 text-[#6E5B3D] leading-relaxed">
                    Your inquiry is on its way. We personally review every request and will
                    reply within 24 hours with availability and a tailored proposal for your{" "}
                    {form.occasion ? form.occasion.toLowerCase() : "celebration"}.
                </p>
            </div>
        );
    }

    return (
        <form
            data-testid="inquiry-form"
            onSubmit={submit}
            className="bg-white border border-[#E8C4C1] rounded-3xl p-6 sm:p-10 shadow-sm space-y-5"
        >
            <div className="grid sm:grid-cols-2 gap-5">
                <input
                    data-testid="inquiry-name-input"
                    required
                    value={form.name}
                    onChange={set("name")}
                    placeholder="Full name"
                    className={inputCls}
                />
                <input
                    data-testid="inquiry-email-input"
                    required
                    type="email"
                    value={form.email}
                    onChange={set("email")}
                    placeholder="Email address"
                    className={inputCls}
                />
                <input
                    data-testid="inquiry-phone-input"
                    value={form.phone}
                    onChange={set("phone")}
                    placeholder="Phone number"
                    className={inputCls}
                />
                <select
                    data-testid="inquiry-occasion-select"
                    required
                    value={form.occasion}
                    onChange={set("occasion")}
                    className={inputCls}
                >
                    <option value="" disabled>Occasion</option>
                    {OCCASION_OPTIONS.map((o) => (
                        <option key={o} value={o}>{o}</option>
                    ))}
                </select>
                <input
                    data-testid="inquiry-date-input"
                    type="date"
                    value={form.date}
                    onChange={set("date")}
                    className={inputCls}
                    aria-label="Preferred date"
                />
                <input
                    data-testid="inquiry-city-input"
                    required
                    value={form.city}
                    onChange={set("city")}
                    placeholder="Event city (McKinney, Dallas, Frisco…)"
                    className={inputCls}
                />
                <input
                    data-testid="inquiry-guests-input"
                    type="number"
                    min="1"
                    value={form.guests}
                    onChange={set("guests")}
                    placeholder="Guest count"
                    className={inputCls}
                />
                <select
                    data-testid="inquiry-package-select"
                    value={form.packageInterest}
                    onChange={set("packageInterest")}
                    className={inputCls}
                >
                    <option value="" disabled>Package interest</option>
                    {PACKAGE_OPTIONS.map((p) => (
                        <option key={p} value={p}>{p}</option>
                    ))}
                </select>
            </div>
            <textarea
                data-testid="inquiry-message-textarea"
                rows={4}
                value={form.message}
                onChange={set("message")}
                placeholder="Tell us your vision — colors, location ideas, special requests…"
                className={inputCls}
            />
            <button
                data-testid="inquiry-submit-button"
                type="submit"
                disabled={submitting}
                className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-[#554011] hover:bg-[#3D2E0A] disabled:opacity-60 text-[#FAF7F2] px-10 py-3.5 rounded-full font-accent text-xs uppercase tracking-[0.18em] transition-all shadow-md hover:shadow-lg"
            >
                {submitting ? "Sending…" : "Send Inquiry"} <Send className="w-4 h-4" />
            </button>
        </form>
    );
};
